/**
 * 获取数据字段对应的颜色
 * @param {string} fieldName - 数据字段名 (urban_pop, MESH_All, x2_urArea, over_thre)
 * @param {string} direction - 方向 (positive, negative)
 * @returns {string} 十六进制颜色值
 */
export function getFieldColor(fieldName, direction) {
  // 根据字段名和方向返回对应的颜色
  const colorMap = {
    'urban_pop': {
      'negative': 'rgb(255, 107, 107)',
      'positive': '#42A5F5'
    },
    'MESH_All': {
      'negative': 'rgb(255, 223, 223)',
      'positive': '#90CAF9'
    },
    'x2_urArea': {
      'negative': 'rgb(240, 62, 62)',
      'positive': '#1E88E5'
    },
    'over_thre': {
      'negative': 'rgb(255, 182, 182)',
      'positive': '#64B5F6'
    }
  };
  
  return colorMap[fieldName]?.[direction] || '#CCCCCC';
}

/**
 * 获取priority值对应的颜色
 * @param {number} value - priority值 (0-30)
 * @returns {string} 十六进制颜色值
 */
export function getPriorityColor(value) {
  // 更美观的红色渐变方案 - 增强浅色部分的饱和度
  if (value <= 3) return '#FFDFDF';
  if (value <= 6) return '#FFCCCB';
  if (value <= 9) return '#FFB6B6';
  if (value <= 12) return '#FF9E9E';
  if (value <= 15) return '#FF8686';
  if (value <= 18) return '#FF6B6B';
  if (value <= 21) return '#FA5252';
  if (value <= 24) return '#F03E3E';
  if (value <= 27) return '#E03131';
  return '#C92A2A'; // 27-30
}

/**
 * 获取城市分类对应的颜色
 * @param {string} category - 城市分类 (LL, HL, LH, HH)
 * @returns {string} 十六进制颜色值
 */
export function getCategoryColor(category) {
  switch(category) {
    case 'LL': return '#F8FB96'; // 黄色 - Low accessibility - Low equality
    case 'HL': return '#92C67C'; // 绿色 - High accessibility - Low equality
    case 'LH': return '#E6BB84'; // 棕色 - Low accessibility - High equality
    case 'HH': return '#E8BEFF'; // 紫色 - High accessibility - High equality
    default: return '#CCCCCC';   // 默认灰色
  }
}

/**
 * 根据Excel数据生成地图显示用的城市数据
 * @param {Object} excelData - Excel中的城市数据
 * @param {number|null} quadrant - 象限值(1-4)，如果为null则不显示分类
 * @param {string} scenario - SSP场景，用于获取accessibility和disparity数据
 * @param {Object|null} accessibilityData - accessibility和disparity数据 {accessibility: number, disparity: number}
 * @returns {Object} 包含显示信息的城市数据对象
 */
export function generateCityDisplayData(excelData, quadrant = null, scenario = 'SSP2', accessibilityData = null) {
  if (!excelData) return null;
  
  const { priorityValue, priorityDirection, synergyData, topVariableValue } = excelData;
  
  // 获取对应的颜色
  const priorityColor = getFieldColor(priorityValue, priorityDirection);
  
  // 使用真实的象限数据而不是模拟数据
  let category = null;
  let categoryColor = '#CCCCCC';
  
  if (quadrant !== null) {
    // 象限值转换为分类代码
    switch(quadrant) {
      case 1: 
        category = 'LL';
        break;
      case 2: 
        category = 'HL';
        break;
      case 3: 
        category = 'LH';
        break;
      case 4: 
        category = 'HH';
        break;
      default:
        category = 'LL';
        break;
    }
    categoryColor = getCategoryColor(category);
  }
  
  // 使用真实的accessibility和disparity数据
  let accessibility = 0;
  let disparity = 0;
  
  if (accessibilityData) {
    accessibility = Math.round(accessibilityData.accessibility * 100) / 100;
    disparity = Math.round(accessibilityData.disparity * 100) / 100;
  }
  
  // 构建factors和rank数据（基于真实的synergy数据）
  const factors = { ...synergyData };
  
  // 根据synergy值的绝对值大小排序
  const rank = Object.keys(synergyData).sort((a, b) => {
    return Math.abs(synergyData[b]) - Math.abs(synergyData[a]);
  });
  
  // 构建direction对象
  const direction = {};
  Object.keys(synergyData).forEach(key => {
    direction[key] = synergyData[key] >= 0 ? 'positive' : 'negative';
  });
  
  return {
    priorityValue: priorityValue,
    priorityDirection: priorityDirection,
    priorityColor: priorityColor,
    category: category,
    categoryColor: categoryColor,
    accessibility: accessibility,
    disparity: disparity,
    factors: factors,
    rank: rank,
    direction: direction,
    topVariableValue: topVariableValue,
    excelData: excelData
  };
} 